import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, ScrollView, Alert, Modal } from 'react-native';
import { Text, Card, Searchbar, FAB, ActivityIndicator, Chip, Divider, Badge, IconButton, Button } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ESTADOS_CONFIG = {
  pendiente:    { label: 'Pendiente',    color: '#FFC107', icon: 'time',              textColor: '#000' },
  preparado:    { label: 'Preparado',    color: '#17A2B8', icon: 'checkmark-done',    textColor: '#fff' },
  en_ruta:      { label: 'En Ruta',      color: '#6F42C1', icon: 'car',               textColor: '#fff' },
  entregado:    { label: 'Entregado',    color: '#28A745', icon: 'checkmark-circle',  textColor: '#fff' },
  completada:   { label: 'Completada',   color: '#28A745', icon: 'checkmark-circle',  textColor: '#fff' },
  cancelada:    { label: 'Cancelada',    color: '#DC3545', icon: 'close-circle',      textColor: '#fff' },
  devuelto:     { label: 'Devuelto',     color: '#FD7E14', icon: 'return-down-back',  textColor: '#fff' },
  parcialmente_devuelto: { label: 'Parc. Devuelto', color: '#FD7E14', icon: 'return-down-back', textColor: '#fff' },
};

const FLUJO_ESTADOS = ['pendiente', 'preparado', 'en_ruta', 'entregado'];

const VentasScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [ventas, setVentas] = useState([]);
  const [filteredVentas, setFilteredVentas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterEstado, setFilterEstado] = useState('todas');
  const [filterCobro, setFilterCobro] = useState('todas'); // todas | cobradas | credito_pendiente
  const [resumenEstados, setResumenEstados] = useState({});
  const [borradores, setBorradores] = useState([]);
  const [showBorradoresModal, setShowBorradoresModal] = useState(false);

  const cargarBorradores = useCallback(async () => {
    try {
      const data = await api.getBorradoresVenta();
      setBorradores(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error al cargar borradores:', e);
    }
  }, []);

  const loadVentas = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      // Solo spinner full-screen en la primera carga; al volver desde un
      // detalle, recargar sin desmontar la lista preserva el scroll.
      if (ventas.length === 0) {
        setLoading(true);
      }
      const [ventasData, resumen] = await Promise.all([
        api.getVentas(),
        api.getResumenEstados()
      ]);

      setVentas(ventasData);
      setResumenEstados(resumen);
      applyFilters(ventasData, searchQuery, filterEstado, filterCobro);
      cargarBorradores();
    } catch (error) {
      console.error('Error al cargar ventas:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, ventas.length, searchQuery, filterEstado, filterCobro]);

  useEffect(() => {
    loadVentas();

    const unsubscribe = navigation.addListener('focus', () => {
      loadVentas();
    });
    return unsubscribe;
  }, [navigation, loadVentas]);

  const onRefresh = () => {
    setRefreshing(true);
    loadVentas();
  };

  // Una venta cuenta como "crédito pendiente" si su CxC todavía tiene saldo.
  // Una venta "cobrada" es: no-crédito O crédito con CxC pagada.
  const tieneCreditoPendiente = (v) =>
    (v.metodo_pago || '').toLowerCase() === 'credito' &&
    parseFloat(v.credito_saldo_pendiente || 0) > 0.01;

  const applyFilters = (data, query, estadoFilter, cobroFilter = filterCobro) => {
    let filtered = [...data];

    if (estadoFilter !== 'todas') {
      filtered = filtered.filter(v => v.estado === estadoFilter);
    }

    if (cobroFilter === 'cobradas') {
      filtered = filtered.filter(v => !tieneCreditoPendiente(v));
    } else if (cobroFilter === 'credito_pendiente') {
      filtered = filtered.filter(v => tieneCreditoPendiente(v));
    }

    if (query.trim() !== '') {
      const q = query.toLowerCase();
      filtered = filtered.filter(venta => {
        const clienteNombre = venta.cliente_nombre || '';
        return clienteNombre.toLowerCase().includes(q) ||
          venta.id.toString().includes(q) ||
          (venta.total !== undefined && venta.total.toString().includes(q));
      });
    }

    setFilteredVentas(filtered);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(ventas, query, filterEstado, filterCobro);
  };

  const handleFilterChange = (filter) => {
    setFilterEstado(filter);
    applyFilters(ventas, searchQuery, filter, filterCobro);
  };

  const handleCobroFilterChange = (filter) => {
    setFilterCobro(filter);
    applyFilters(ventas, searchQuery, filterEstado, filter);
  };

  const getEstadoConfig = (estado) => ESTADOS_CONFIG[estado] || ESTADOS_CONFIG.pendiente;

  const getProgresoEstado = (estado) => {
    const idx = FLUJO_ESTADOS.indexOf(estado);
    if (idx === -1) return -1; // cancelada, devuelto
    return idx;
  };

  const formatFecha = (fecha) => {
    const date = new Date(fecha);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Hoy ${date.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Ayer ${date.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('es-HN', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    }
  };

  const renderProgressBar = (estado) => {
    const progreso = getProgresoEstado(estado);
    if (progreso === -1) return null;

    return (
      <View style={styles.progressContainer}>
        {FLUJO_ESTADOS.map((e, i) => {
          const config = getEstadoConfig(e);
          const isActive = i <= progreso;
          const isLast = i === FLUJO_ESTADOS.length - 1;
          return (
            <React.Fragment key={e}>
              <View style={styles.progressStep}>
                <View style={[
                  styles.progressDot,
                  { backgroundColor: isActive ? config.color : '#DDD' }
                ]}>
                  {isActive && <Ionicons name="checkmark" size={10} color="#fff" />}
                </View>
                <Text style={[styles.progressLabel, isActive && { color: config.color, fontWeight: 'bold' }]}>
                  {config.label.substring(0, 4)}
                </Text>
              </View>
              {!isLast && (
                <View style={[
                  styles.progressLine,
                  { backgroundColor: i < progreso ? getEstadoConfig(FLUJO_ESTADOS[i + 1]).color : '#DDD' }
                ]} />
              )}
            </React.Fragment>
          );
        })}
      </View>
    );
  };

  const renderVentaItem = ({ item }) => {
    const estadoConfig = getEstadoConfig(item.estado);

    return (
      <TouchableOpacity onPress={() => navigation.navigate('DetalleVenta', { venta: item })}>
        <Card style={styles.ventaCard}>
          <Card.Content>
            <View style={styles.ventaHeader}>
              <View style={styles.ventaInfo}>
                <View style={styles.ventaIdRow}>
                  <Text style={styles.ventaId}>Pedido #{item.id}</Text>
                  <View style={[styles.estadoChip, { backgroundColor: estadoConfig.color }]}>
                    <Ionicons name={estadoConfig.icon} size={12} color={estadoConfig.textColor} style={styles.estadoIcon} />
                    <Text style={[styles.estadoText, { color: estadoConfig.textColor }]}>{estadoConfig.label.toUpperCase()}</Text>
                  </View>
                </View>
                <Text style={styles.ventaCliente}>
                  <Ionicons name="person" size={14} color="#666" /> {item.cliente_nombre || 'Cliente desconocido'}
                </Text>
                {item.vendedor_nombre && (
                  <Text style={styles.ventaVendedor}>
                    <Ionicons name="business" size={14} color="#666" /> {item.vendedor_nombre}
                  </Text>
                )}
                {item.cotizacion_numero && (
                  <Text style={styles.ventaCotizacion}>
                    <Ionicons name="document-text" size={14} color="#17A2B8" /> Cot. {item.cotizacion_numero}
                  </Text>
                )}
              </View>
              <View style={styles.ventaTotalContainer}>
                <Text style={styles.ventaTotal}>L. {parseFloat(item.total).toFixed(2)}</Text>
                <Text style={styles.metodoPago}>
                  <Ionicons name={item.metodo_pago === 'efectivo' ? 'cash' : item.metodo_pago === 'pendiente' ? 'time' : 'card'} size={12} color="#666" />
                  {' '}{(item.metodo_pago || 'pendiente').toUpperCase()}
                </Text>
                {tieneCreditoPendiente(item) && (
                  <View style={styles.creditoPendienteBadge}>
                    <Ionicons name="time" size={10} color="#fff" />
                    <Text style={styles.creditoPendienteText}>
                      Por cobrar L. {parseFloat(item.credito_saldo_pendiente).toFixed(2)}
                    </Text>
                  </View>
                )}
                {(item.metodo_pago || '').toLowerCase() === 'credito' && !tieneCreditoPendiente(item) && (
                  <View style={styles.creditoPagadoBadge}>
                    <Ionicons name="checkmark-circle" size={10} color="#fff" />
                    <Text style={styles.creditoPagadoText}>Crédito cobrado</Text>
                  </View>
                )}
              </View>
            </View>

            {renderProgressBar(item.estado)}

            <Divider style={styles.divider} />

            <View style={styles.ventaFooter}>
              <Text style={styles.ventaFecha}>
                <Ionicons name="calendar" size={12} color="#888" /> {formatFecha(item.fecha)}
              </Text>
              <Text style={styles.ventaItems}>
                <Ionicons name="cube" size={12} color="#888" /> {parseInt(item.total_productos || 0)} productos
              </Text>
            </View>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  const estadoFilters = [
    { key: 'todas', label: 'Todas', color: '#007bff' },
    { key: 'pendiente', label: 'Pendientes', color: '#FFC107' },
    { key: 'preparado', label: 'Preparados', color: '#17A2B8' },
    { key: 'en_ruta', label: 'En Ruta', color: '#6F42C1' },
    { key: 'entregado', label: 'Entregados', color: '#28A745' },
    { key: 'completada', label: 'Completadas', color: '#28A745' },
    { key: 'cancelada', label: 'Canceladas', color: '#DC3545' },
  ];

  const formatFechaCorta = (f) => {
    if (!f) return '';
    try { return new Date(f).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch (e) { return f; }
  };

  const descartarBorrador = (b) => {
    Alert.alert(
      'Descartar borrador',
      `¿Eliminar el borrador de ${b.cliente_nombre || 'cliente'}?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Descartar', style: 'destructive', onPress: async () => {
            try {
              await api.eliminarBorradorVenta(b.id);
              cargarBorradores();
            } catch (e) {
              Alert.alert('Error', e.message || 'No se pudo descartar');
            }
          }
        }
      ]
    );
  };

  const retomarBorrador = (b) => {
    setShowBorradoresModal(false);
    navigation.navigate('NuevaVenta', { borrador_id: b.id });
  };

  return (
    <View style={styles.container}>
      {borradores.length > 0 && (
        <TouchableOpacity style={styles.borradoresBanner} onPress={() => setShowBorradoresModal(true)}>
          <Ionicons name="document-text-outline" size={18} color="#0066cc" />
          <Text style={styles.borradoresBannerText}>
            Tienes {borradores.length} borrador{borradores.length === 1 ? '' : 'es'} sin procesar
          </Text>
          <Text style={styles.borradoresBannerVer}>Ver →</Text>
        </TouchableOpacity>
      )}

      <Searchbar
        placeholder="Buscar pedido..."
        onChangeText={handleSearch}
        value={searchQuery}
        style={styles.searchBar}
      />

      <Modal visible={showBorradoresModal} animationType="slide" onRequestClose={() => setShowBorradoresModal(false)}>
        <View style={styles.borradoresModalContainer}>
          <View style={styles.borradoresModalHeader}>
            <Text style={styles.borradoresModalTitle}>Borradores ({borradores.length})</Text>
            <IconButton icon="close" size={26} onPress={() => setShowBorradoresModal(false)} />
          </View>
          <FlatList
            data={borradores}
            keyExtractor={(item) => item.id.toString()}
            ItemSeparatorComponent={() => <Divider />}
            renderItem={({ item }) => (
              <View style={styles.borradorRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.borradorCliente}>{item.cliente_nombre || 'Sin cliente'}</Text>
                  <Text style={styles.borradorMeta}>
                    {item.total_productos || 0} ítem{(item.total_productos || 0) === 1 ? '' : 's'} · L. {parseFloat(item.total || 0).toFixed(2)}
                  </Text>
                  <Text style={styles.borradorFecha}>{formatFechaCorta(item.fecha)}</Text>
                </View>
                <Button mode="contained" compact onPress={() => retomarBorrador(item)} style={{ marginRight: 4 }}>
                  Retomar
                </Button>
                <IconButton icon="delete" iconColor="#DC3545" onPress={() => descartarBorrador(item)} />
              </View>
            )}
            ListEmptyComponent={
              <View style={{ padding: 40, alignItems: 'center' }}>
                <Text style={{ color: '#888' }}>No tienes borradores</Text>
              </View>
            }
          />
        </View>
      </Modal>

      {/* Filtros por estado de cobro */}
      <View style={styles.cobroFiltersContainer}>
        {[
          { key: 'todas', label: 'Todas', color: '#007bff' },
          { key: 'cobradas', label: 'Cobradas', color: '#28a745' },
          { key: 'credito_pendiente', label: 'Crédito pend.', color: '#e67e22' },
        ].map(f => {
          const isSelected = filterCobro === f.key;
          const count = f.key === 'todas'
            ? ventas.length
            : f.key === 'credito_pendiente'
              ? ventas.filter(tieneCreditoPendiente).length
              : ventas.filter(v => !tieneCreditoPendiente(v)).length;
          return (
            <TouchableOpacity
              key={f.key}
              onPress={() => handleCobroFilterChange(f.key)}
              style={[
                styles.cobroFilterChip,
                isSelected && { backgroundColor: f.color, borderColor: f.color }
              ]}
            >
              <Text style={[styles.cobroFilterChipText, isSelected && { color: '#fff' }]}>
                {f.label} ({count})
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <View style={styles.filtersContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {estadoFilters.map(f => {
            const count = f.key === 'todas'
              ? ventas.length
              : (resumenEstados[f.key] || 0);
            const isSelected = filterEstado === f.key;

            return (
              <TouchableOpacity
                key={f.key}
                onPress={() => handleFilterChange(f.key)}
                style={[
                  styles.filterChip,
                  isSelected && { backgroundColor: f.color, borderColor: f.color }
                ]}
              >
                <Text style={[
                  styles.filterChipText,
                  isSelected && { color: f.key === 'pendiente' ? '#000' : '#fff' }
                ]}>
                  {f.label} ({count})
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007bff" />
          <Text style={styles.loadingText}>Cargando pedidos...</Text>
        </View>
      ) : (
        <>
          <FlatList
            data={filteredVentas}
            keyExtractor={(item) => item.id.toString()}
            renderItem={renderVentaItem}
            contentContainerStyle={styles.listContainer}
            ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Ionicons name="cart-outline" size={64} color="#ccc" />
                <Text style={styles.emptyText}>No se encontraron pedidos</Text>
              </View>
            }
          />

          <FAB
            style={[styles.fab, { marginBottom: insets.bottom }]}
            icon="plus"
            onPress={() => navigation.navigate('NuevaVenta')}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  searchBar: {
    margin: 8,
    elevation: 2,
  },
  borradoresBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#E7F1FF', paddingHorizontal: 12, paddingVertical: 10,
    gap: 8, borderBottomWidth: 1, borderBottomColor: '#cfe2ff',
  },
  borradoresBannerText: { flex: 1, color: '#0066cc', fontSize: 13, fontWeight: '600' },
  borradoresBannerVer: { color: '#0066cc', fontWeight: '700' },
  borradoresModalContainer: { flex: 1, backgroundColor: '#fff' },
  borradoresModalHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#eee',
  },
  borradoresModalTitle: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  borradorRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 12, paddingVertical: 10,
  },
  borradorCliente: { fontSize: 15, fontWeight: '600', color: '#212529' },
  borradorMeta: { fontSize: 13, color: '#495057', marginTop: 2 },
  borradorFecha: { fontSize: 11, color: '#868e96', marginTop: 2 },
  filtersContainer: {
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#DDD',
    backgroundColor: '#fff',
  },
  filterChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
  },
  listContainer: {
    padding: 8,
  },
  ventaCard: {
    elevation: 2,
    marginBottom: 2,
  },
  ventaHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  ventaInfo: {
    flex: 1,
    marginRight: 10,
  },
  ventaIdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  ventaId: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 8,
  },
  estadoChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  estadoIcon: {
    marginRight: 3,
  },
  estadoText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  ventaCliente: {
    fontSize: 13,
    color: '#666',
    marginBottom: 2,
  },
  ventaVendedor: {
    fontSize: 12,
    color: '#888',
    marginBottom: 2,
  },
  ventaCotizacion: {
    fontSize: 12,
    color: '#17A2B8',
    marginBottom: 2,
    fontWeight: '500',
  },
  ventaTotalContainer: {
    alignItems: 'flex-end',
  },
  ventaTotal: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#28a745',
    marginBottom: 2,
  },
  metodoPago: {
    fontSize: 11,
    color: '#666',
  },
  creditoPendienteBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e67e22',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginTop: 3,
    gap: 3,
  },
  creditoPendienteText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
  },
  creditoPagadoBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#28a745',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginTop: 3,
    gap: 3,
  },
  creditoPagadoText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
  },
  cobroFiltersContainer: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    marginBottom: 4,
    gap: 6,
  },
  cobroFilterChip: {
    flex: 1,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  cobroFilterChipText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#666',
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 4,
    paddingHorizontal: 4,
  },
  progressStep: {
    alignItems: 'center',
  },
  progressDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressLabel: {
    fontSize: 9,
    color: '#999',
    marginTop: 2,
  },
  progressLine: {
    flex: 1,
    height: 3,
    marginHorizontal: 2,
    marginBottom: 14,
    borderRadius: 2,
  },
  divider: {
    marginVertical: 8,
  },
  ventaFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  ventaFecha: {
    fontSize: 12,
    color: '#888',
  },
  ventaItems: {
    fontSize: 12,
    color: '#888',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 50,
  },
  emptyText: {
    marginTop: 10,
    color: '#888',
    fontSize: 16,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
    backgroundColor: '#007bff',
  },
});

export default VentasScreen;
